import { TreeViewBaseItem, TreeViewItemId } from "@mui/x-tree-view";
import { SyntheticEvent, useCallback, useEffect, useMemo, useState, MouseEvent } from "react";
import { useGetModelFamilyTagHierarchyCountsModelFamilyTagsHierarchyCountsGet } from "../api/generated/fastAPI";
import { generateRandomString, splitLabel } from "../utils/formattingFns";
import { arrayMove } from "@dnd-kit/sortable";

export interface ExtendedTreeViewBase extends TreeViewBaseItem {
    count: number,
    tagCategory: string,
    color: string,
}

export interface TreeSelectedCategory {
    id: string,
    value: string,
    label: string,
    color: string,
}

function getRandomMutedHexColor() {
  // Generate random values for R, G, and B components,
  // keeping them within a muted range (e.g., 80-180 for a softer feel).
  const r = Math.floor(Math.random() * 101) + 80; // 80-180
  const g = Math.floor(Math.random() * 101) + 80; // 80-180
  const b = Math.floor(Math.random() * 101) + 80; // 80-180

  // Convert each component to a two-digit hexadecimal string
  // and pad with a leading '0' if necessary.
  const toHex = (c: number) => c.toString(16).padStart(2, '0');

  // Combine the hex components with '#' prefix.
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

const organizingValueColors = [
    '#4C72B0',
    '#DD8453',
    '#55A868',
    '#C44F51',
    '#8272B3',
    '#937860',
    '#DA8BC3',
    '#8C8C8C',
    '#CCB974',
    '#64B5CD',
];

function delay(ms: number) {
    return new Promise(res => setTimeout(res, ms));
};

const asyncConstructTree = async (
    selectedCategories: TreeSelectedCategory[],
    items: any[],
    parentId: string | null = null,
) => {

    await delay(0);

    const tree: ExtendedTreeViewBase[] = [];
    const promises = items.map(async (item) => {
        const [ id, { children, count } ] = item;
        const [ tagCategory, label ] = id.split('::');
        const matchingCategory = selectedCategories.find(c => c.value === tagCategory);
        const color = matchingCategory ? matchingCategory.color : '';

        const childList = Object.entries(children);
        const parentTags = JSON.parse(parentId ?? '[]');
        const allTags = parentTags.flat(Infinity).concat([id]).map((tag: string) => ([tag]));
        const updatedId = JSON.stringify(allTags);

        if (childList.length === 0) {

            tree.push({
                label,
                id: updatedId,
                children: [],
                count,
                tagCategory,
                color
            });
        } else {
            const itemChildren = await asyncConstructTree(selectedCategories, childList, updatedId);
            tree.push({
                label,
                id: updatedId,
                children: itemChildren,
                count,
                tagCategory,
                color
            });
        }
    })
    
    await Promise.all(promises);

    if (selectedCategories.length > 0) {
        const selectedTagGroups = selectedCategories.map(c => c.value);
        tree.sort((a,b) => {
            const aCategoryIndex = selectedTagGroups.indexOf(a.tagCategory);
            const bCategoryIndex = selectedTagGroups.indexOf(b.tagCategory);

            if (aCategoryIndex < bCategoryIndex) {
                return -1;
            } else if (aCategoryIndex > bCategoryIndex) {
                return 1;
            } else {
                if (a.label.toLocaleLowerCase() < b.label.toLocaleLowerCase()) {
                    return -1
                } else if (a.label.toLocaleLowerCase() > b.label.toLocaleLowerCase()) {
                    return 1
                } else {
                    return 0;
                }
                // return 0;
            }
        });
    }

    return tree;
}

const asyncFilterItemsByCategory = async (
    searchKey: string,
    data: any[],
) => {

    await delay(0);

    const tree: any[] = [];

    const promises = data.map(async (item) => {
        const label = item.label as string;
        const matches = label.toLocaleLowerCase().includes(searchKey.toLocaleLowerCase());

        if (matches) {
            tree.push(item);
        } else {
            const children = item.children.length > 0 ? await asyncFilterItemsByCategory(searchKey, item.children) : [];

            if (children.length > 0) {
                tree.push({
                    ...item,
                    children,
                });
            }
        }
    });

    await Promise.all(promises);

    return tree;

}


const getAllItemsWithChildrenItemIds = (allItems: any[]) => {
  const itemIds: TreeViewItemId[] = [];
  const registerItemId = (item: any) => {
    if (item.children?.length) {
      itemIds.push(item.id);
      item.children.forEach(registerItemId);
    }
  };

  allItems.forEach(registerItemId);

  return itemIds;
};

const getSelectedItemPath = (data: any, targetId: string) => {
    if (data.id === targetId) return [data];

    if (data.children || Array.isArray(data)) {
        let children = Array.isArray(data) ? data : data.children;
        for (let child of children) {
            let result = getSelectedItemPath(child, targetId) as any[] | undefined;
            if (result) {
                if (data.id) result.unshift(data);
                return result;
            }
        }
    }
};

const useModelFamilyTree = ({
    tag_groups,
    updateModelsByHierarchy,
}: {
    tag_groups?: { [key: string]: string[] },
    updateModelsByHierarchy: (tag_type_hierarchy: string[] | null, tag_hierarchy: string | null) => void
}) => {
    const [ selectedCategories, setSelectedCategories ] = useState<TreeSelectedCategory[]>([]);

    const tag_type_hierarchy: string[] = useMemo(() => {
        if (selectedCategories.length === 0) return [];

        return selectedCategories.map(cat => cat.value);
    }, [selectedCategories]);


    const { data: hierarchyData, isLoading: isHierarchyDataLoading } = useGetModelFamilyTagHierarchyCountsModelFamilyTagsHierarchyCountsGet({
        tag_type_hierarchy,
    });


    const [activeId, setActiveId] = useState(null);
    const [ isTreeLoading, setIsTreeLoading ] = useState(false);
    const [ allTreeItems, setAllTreeItems ] = useState<ExtendedTreeViewBase[]>([]);
    const [ filteredTreeItems, setFilteredTreeItems ] = useState<ExtendedTreeViewBase[]>([]);
    const [ selectedItemPath, setSelectedItemPath ] = useState<ExtendedTreeViewBase[]>([]);

    const [ expandedItems, setExpandedItems ] = useState<string[]>([]);
    const [ selectedTreeItem, setSelectedTreeItem ] = useState<string | null>(null);

    const countData = useMemo(() => {
        if (!hierarchyData) return {};
        return hierarchyData as unknown as any;
    }, [hierarchyData]);

    const tagCategories: { value: string, label: string, color: string }[] = useMemo(() => {
        if (!tag_groups || Object.keys(tag_groups).length === 0) return [];

        const categories: { value: string, label: string, color: string }[] = Object.keys(tag_groups).map((group, i) => ({
            value: group,
            label: splitLabel(group),
            color: organizingValueColors[i] ?? getRandomMutedHexColor(),
        }));

        categories.sort((a, b) => {
            if (a.value < b.value) {
                return -1;
            } else if (a.value > b.value) {
                return 1;
            } else {
                return 0;
            }
        });

        return categories;

    }, [tag_groups]);

    useEffect(() => {
        let category: TreeSelectedCategory = { value: '', label: '', id: generateRandomString(), color: '' };
        if (tagCategories.length > 0) {
            const targetHierarchy = tagCategories.find(category => category.value === 'target_hierarchy');

            if (targetHierarchy) {
                const { value, label, color } = targetHierarchy;
                category.value = value;
                category.label = label;
                category.color = color;
            } else {
                const { value, label, color } = tagCategories[0];
                category.value = value;
                category.label = label;
                category.color = color;
            }
        };

        setSelectedCategories((previous) => {
            return [category];
        });
    }, [tagCategories]);

    useEffect(() => {
        const getTreeItems = async (categories: TreeSelectedCategory[], count: any[]) => {
            setIsTreeLoading(true);

            const result = await asyncConstructTree(categories, count);
            setAllTreeItems(result);
            setFilteredTreeItems(result);

            setIsTreeLoading(false);
        }

        const counts = Object.entries(countData);
        if (counts.length > 0) getTreeItems(selectedCategories, counts);
        
    }, [selectedCategories, countData]);

    const allItemIds = useMemo(() => {
        if (filteredTreeItems.length === 0) return [];
        return getAllItemsWithChildrenItemIds(filteredTreeItems);
    }, [filteredTreeItems]);

    function handleDragStart(event: any) {
        const {active} = event;

        setActiveId(active.id);
    }

    function handleDragEnd(event: any) {
        const {active, over} = event;

        const activeContent = selectedCategories.find(c => c.id === active.id);
        const overContent = selectedCategories.find(c => c.id === over.id);
        
        if ((active.id !== over.id) && activeContent && overContent) {
            
            setSelectedCategories((items) => {
                const oldIndex = items.indexOf(activeContent);
                const newIndex = items.indexOf(overContent);
                
                return arrayMove(items, oldIndex, newIndex);
            });
        }

        setActiveId(null);
    };

    const handleSelectCategory = (e: any, name: string) => {
        const { value, label, color } = e;

        const matchingCategory = selectedCategories.find(category => category.id === name);
        if (matchingCategory) {
            setSelectedCategories((prevItems) => prevItems.map((item) => {
                if (item.id === matchingCategory.id) {
                    return {
                        ...item,
                        value,
                        label,
                        color,
                    }
                } else {
                    return {
                        ...item,
                    }
                }
            }));
        }
    }

    const handleAddCategory = () => {
        setSelectedCategories((categories) => {
            return categories.concat({ id: generateRandomString(), value: '', label: '', color: '' });
        })
    };

    const handleRemoveCategory = (id: string) => {
        setSelectedCategories((categories) => {
            return categories.filter(category => category.id !== id);
        });
    };

    // const selectedItemTreePath: ExtendedTreeViewBase[] = useMemo(() => {
    //     if (!selectedTreeItem) return [];

    //     const path: ExtendedTreeViewBase[] = getSelectedItemPath(allTreeItems, selectedTreeItem) ?? [];

    //     return path;

    // }, [selectedTreeItem, allTreeItems]);

    const handleSelectItemFromTree = (
        e: SyntheticEvent | null,
        id: string | null,
    ) => {
        setSelectedTreeItem(id);

        let tag_hierarchy: string | null = null;
        if (id) {
            const parsedId = JSON.parse(id);
            if (Array.isArray(parsedId)) {
                tag_hierarchy = parsedId.flat(Infinity).join('++');
            }

            const path: ExtendedTreeViewBase[] = getSelectedItemPath(allTreeItems, id) ?? [];
            setSelectedItemPath(path);
        };

        updateModelsByHierarchy(tag_type_hierarchy, tag_hierarchy ? encodeURIComponent(tag_hierarchy) : null);
    }

    const handleSelectItemFromPath = (
        e: MouseEvent<HTMLButtonElement>,
        id: string | null,
    ) => {
        e.preventDefault();
        setSelectedTreeItem(id);

        let tag_hierarchy: string | null = null;
        if (id) {
            const parsedId = JSON.parse(id);
            if (Array.isArray(parsedId)) {
                tag_hierarchy = parsedId.flat(Infinity).join('++');
            }
        };

        let path: ExtendedTreeViewBase[] = [];

        if (id) {
            path = selectedItemPath.slice(0, selectedItemPath.map(item => item.id).indexOf(id)+1);
        }

        setSelectedItemPath(path);

        updateModelsByHierarchy(tag_type_hierarchy, tag_hierarchy ? encodeURIComponent(tag_hierarchy) : null)

    }


    const handleSearch = useCallback(async (searchKey: string, value: string) => {
        if (allTreeItems.length > 0) {
            setIsTreeLoading(true);
            const items = await asyncFilterItemsByCategory(value, allTreeItems);
            setFilteredTreeItems(items);
            setIsTreeLoading(false);
        }
    }, [allTreeItems]);

    const handleExpandedItemsChange = (
        event: React.SyntheticEvent | null,
        itemIds: string[],
    ) => {
        setExpandedItems(itemIds);
    };

    const handleExpandAll = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setExpandedItems(allItemIds);
    };

    const handleCollapseAll = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setExpandedItems([]);
    }


    return {
        selectedCategories,
        isHierarchyDataLoading,
        isTreeLoading,
        activeId,
        filteredTreeItems,
        expandedItems,
        selectedTreeItem,
        handleDragStart,
        handleDragEnd,
        handleSelectCategory,
        handleAddCategory,
        handleRemoveCategory,
        selectedItemPath,
        handleSelectItemFromTree,
        handleSelectItemFromPath,
        handleSearch,
        handleExpandedItemsChange,
        handleExpandAll,
        handleCollapseAll,
        allItemIds,
        tagCategories,
    }
};

export default useModelFamilyTree;